import React, { useMemo } from 'react';
import './ContentFigure.css';

/**
 * A figure inside lesson content or a question.
 *
 * Two sources, deliberately in this order:
 *
 *   svg  — inline vector, authored as text in content_data. Preferred for
 *          diagrams: it is crisp at any size, needs no storage or CDN, costs
 *          no extra request, and — because it can use `currentColor` — it
 *          follows dark mode instead of being a white rectangle in it. Most
 *          UAT figures are timelines, axes and simple geometry, which is
 *          exactly what SVG is for.
 *   url  — a raster image, for the cases a diagram cannot be drawn (a
 *          photograph, a scanned figure from a real paper).
 *
 * ## Why the sanitiser exists
 *
 * Inline SVG is markup, and this renders it with dangerouslySetInnerHTML. Even
 * though content is authored by us rather than by students, "our own database"
 * is not a security boundary — a compromised admin path or a bad generator
 * output would become script execution in every student's browser. So the
 * markup is stripped of the things that can execute before it is mounted:
 * <script>, <foreignObject> (which can host arbitrary HTML), event handlers,
 * and javascript: URLs.
 *
 * This is a denylist, which is weaker than a parser-based allowlist. It is
 * chosen because the alternative — adding DOMPurify — is a dependency decision
 * that should be made deliberately rather than smuggled in with a figure
 * component. If figures ever accept third-party content, replace this.
 */

const DANGEROUS_TAGS = /<\s*(script|foreignObject|iframe|object|embed|link|style)[\s\S]*?>[\s\S]*?<\s*\/\s*\1\s*>/gi;
const SELF_CLOSING_DANGEROUS = /<\s*(script|foreignObject|iframe|object|embed|link)[^>]*\/?>/gi;
const EVENT_ATTRS = /\son[a-z]+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi;
const JS_URLS = /(?:href|xlink:href|src)\s*=\s*(?:"|')?\s*javascript:[^"'>\s]*/gi;

function sanitiseSvg(raw: string): string {
  return raw
    .replace(DANGEROUS_TAGS, '')
    .replace(SELF_CLOSING_DANGEROUS, '')
    .replace(EVENT_ATTRS, '')
    .replace(JS_URLS, '');
}

export type FigureData = {
  svg?: string;
  url?: string;
  /** Alias for `url`. `src` is the obvious name for an image source and an
   *  importer reached for it once, which produced 39 questions that asked
   *  "which picture..." and rendered no picture — a figure that exists in the
   *  data and nowhere on screen. Accepted here so that mistake shows up as a
   *  figure rather than as silence. */
  src?: string;
  alt?: string;
  caption?: string;
  /** Cap the rendered width so a small diagram is not stretched across a
   *  desktop column, where it reads as clip art rather than a figure. */
  max_width?: number;
};

const ContentFigure: React.FC<{ figure?: FigureData | null }> = ({ figure }) => {
  const clean = useMemo(
    () => (figure?.svg ? sanitiseSvg(figure.svg) : null),
    [figure?.svg]
  );

  const raster = figure?.url || figure?.src;
  if (!figure || (!clean && !raster)) return null;

  return (
    <figure
      className="cfig"
      style={figure.max_width ? { maxWidth: `${figure.max_width}px` } : undefined}
    >
      {clean ? (
        <div
          className="cfig-svg"
          role="img"
          aria-label={figure.alt || figure.caption || 'Figure'}
          dangerouslySetInnerHTML={{ __html: clean }}
        />
      ) : (
        <img
          className="cfig-img"
          src={raster}
          alt={figure.alt || figure.caption || ''}
          loading="lazy"
        />
      )}
      {figure.caption && <figcaption className="cfig-cap">{figure.caption}</figcaption>}
    </figure>
  );
};

export default ContentFigure;
